import { type ReactNode, useCallback, useId, useState } from "react";
import { Glyph } from "~/components/ui/glyph";
import { useI18n, useT } from "~/i18n";

export function Panel({
  title,
  hint,
  actions,
  footer,
  flush,
  children,
  headingLevel = 2,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  children: ReactNode;
  headingLevel?: 2 | 3 | 4;
}) {
  const Heading = `h${headingLevel}` as const;
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel__header">
          {title && <Heading className="panel__title">{title}</Heading>}
          {hint && <span className="panel__hint">{hint}</span>}
          {actions && <div className="panel__actions">{actions}</div>}
        </div>
      )}
      <div className={flush ? "panel__body panel__body--flush" : "panel__body"}>{children}</div>
      {footer && <div className="panel__footer">{footer}</div>}
    </section>
  );
}

export function Metric({
  label,
  value,
  meta,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: "neutral" | "positive" | "caution" | "critical";
}) {
  return (
    <div className="metric" data-tone={tone}>
      <span className="metric__label">{label}</span>
      <span className="metric__value tnum">{value}</span>
      {meta && <span className="metric__meta">{meta}</span>}
    </div>
  );
}

export function Badge({
  children,
  variant,
  title,
}: {
  children: ReactNode;
  variant?: "immutable" | "four-eyes";
  title?: string;
}) {
  return (
    <span className={variant ? `badge badge--${variant}` : "badge"} title={title}>
      {children}
    </span>
  );
}

/**
 * Immutability is a domain guarantee, so it gets a persistent, explicit cue
 * rather than an inferred "you can't edit this because nothing is clickable".
 */
export function ImmutableBadge({ digest }: { digest?: string }) {
  const t = useT();
  return (
    <Badge variant="immutable" title={t("rulesets.immutable.hint")}>
      <Glyph name="lock" />
      <span>{t("rulesets.immutable")}</span>
      {digest && <span className="mono text-2xs">{shortDigest(digest)}</span>}
    </Badge>
  );
}

export function FourEyesBadge({
  approved,
  required,
}: {
  approved: number;
  required: number;
}) {
  const { t, tPlural } = useI18n();
  return (
    <Badge variant="four-eyes" title={t("proposals.fourEyes.needed")}>
      <Glyph name="people" />
      <span>{tPlural("proposals.fourEyes.count", required, { approved, required })}</span>
    </Badge>
  );
}

export function shortDigest(digest: string): string {
  const value = digest.startsWith("sha256:") ? digest.slice(7) : digest;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

/**
 * Copy-to-clipboard for the values operators paste into tickets: digests,
 * correlation ids, calculation ids. The confirmation is announced, not just
 * coloured, and the full value stays available to assistive technology.
 */
export function Copyable({
  value,
  display,
  label,
  actionLabel,
}: {
  value: string;
  display?: string;
  label?: string;
  actionLabel?: string;
}) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const id = useId();

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  return (
    <span className="copyable">
      {!actionLabel ? (
        <span className="copyable__value mono" id={id} title={value}>
          {display ?? value}
        </span>
      ) : null}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={copy}
        aria-describedby={actionLabel ? undefined : id}
        aria-label={actionLabel ?? `${t("common.copy")}${label ? ` — ${label}` : ""}`}
      >
        <span aria-hidden="true">{copied ? "✓" : "⧉"}</span>{" "}
        {actionLabel ? (copied ? t("common.copied") : actionLabel) : null}
      </button>
      {/* biome-ignore lint/a11y/useSemanticElements: <output> is for calculated
          form results. This is a transient confirmation announcement, which is
          exactly what role="status" describes. */}
      <span role="status" className="sr-only">
        {copied ? t("common.copied") : ""}
      </span>
    </span>
  );
}

export function Digest({ value }: { value: string }) {
  return <Copyable value={value} display={shortDigest(value)} label="digest" />;
}

export function Meter({
  value,
  max = 1,
  tone,
  label,
}: {
  value: number;
  max?: number;
  tone?: "positive" | "caution" | "critical";
  label: string;
}) {
  const { formatPercent } = useI18n();
  const ratio = max === 0 ? 0 : Math.min(1, Math.max(0, value / max));

  return (
    <div
      className="meter"
      role="meter"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${label}: ${formatPercent(ratio)}`}
    >
      <div className="meter__fill" data-tone={tone} style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

/**
 * A 14-point run history. It is decorative: the same numbers are always
 * available as text next to it, so it carries no unique information.
 */
export function Sparkline({ values, label }: { values: number[]; label: string }) {
  const max = Math.max(1, ...values);
  return (
    <div className="spark" role="img" aria-label={`${label}: ${values.join(", ")}`}>
      {values.map((value, index) => (
        <span
          key={`${label}-${index}-${value}`}
          className="spark__bar"
          data-tone={value === 0 ? "critical" : undefined}
          style={{ height: `${Math.max(8, (value / max) * 100)}%` }}
        />
      ))}
    </div>
  );
}

export function Skeleton({ height = 14, width = "100%" }: { height?: number; width?: string }) {
  return <div className="skeleton" style={{ height, width }} aria-hidden="true" />;
}

export function DefinitionList({
  items,
  stacked,
}: {
  items: Array<{ term: ReactNode; description: ReactNode }>;
  stacked?: boolean;
}) {
  return (
    <dl className={stacked ? "dl dl--stacked" : "dl"}>
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rendered from a literal array whose order and length are fixed at the call site; nothing is reordered, inserted or removed.
        <div key={index} style={{ display: "contents" }}>
          <dt>{item.term}</dt>
          <dd>{item.description}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Timeline({
  items,
}: {
  items: Array<{
    id: string;
    when: string;
    title: ReactNode;
    detail?: ReactNode;
    tone?: "positive" | "critical";
  }>;
}) {
  const { formatDateTime } = useI18n();
  return (
    <ol className="timeline">
      {items.map((item) => (
        <li className="timeline__item" key={item.id} data-tone={item.tone}>
          <span className="timeline__when tnum">{formatDateTime(item.when)}</span>
          <span className="text-strong">{item.title}</span>
          {item.detail && <span className="text-muted text-2xs">{item.detail}</span>}
        </li>
      ))}
    </ol>
  );
}
